import Bugsnag from '@bugsnag/js'
import BugsnagPluginReact from '@bugsnag/plugin-react'
import { BUXUS_APP_DISPLAY_VERSION, resolveWebReleaseStage } from '@buxus/shared'
import type { ReactNode } from 'react'
import React from 'react'

let bugsnagStarted = false

/** Bugsnag `appType` / metadata — filter in dashboard and email notifications (e.g. `appType:web`). */
export const BUXUS_BUGSNAG_PLATFORM = 'web' as const

const WEB_ERROR_REPORTED = Symbol.for('buxus.webErrorReported')

/** True when {@link notifyWebError} or storage reporters already sent this error to Bugsnag. */
export function isWebErrorReported(error: unknown): boolean {
  return (
    error !== null &&
    typeof error === 'object' &&
    (error as Record<symbol, unknown>)[WEB_ERROR_REPORTED] === true
  )
}

export function markWebErrorReported(error: unknown): void {
  if (error !== null && typeof error === 'object') {
    Object.defineProperty(error, WEB_ERROR_REPORTED, {
      value: true,
      enumerable: false,
      configurable: true,
    })
  }
}

function releaseStageFromMode(mode: string): string {
  return resolveWebReleaseStage({
    viteMode: mode,
    envOverride: import.meta.env.VITE_RELEASE_STAGE,
  })
}

/**
 * Global error reporting for the web SPA. Separate from product analytics (Mixpanel/GA).
 * No-ops when `VITE_BUGSNAG_API_KEY` is unset (local dev without a key).
 */
export function initWebErrorReporting(): void {
  if (bugsnagStarted || typeof window === 'undefined') return

  const apiKey = import.meta.env.VITE_BUGSNAG_API_KEY?.trim()
  if (!apiKey) {
    if (import.meta.env.DEV) {
      console.debug('[errors] Bugsnag disabled — set VITE_BUGSNAG_API_KEY to enable')
    }
    return
  }

  const releaseStage = releaseStageFromMode(import.meta.env.MODE)
  const enableInDev =
    import.meta.env.DEV &&
    import.meta.env.VITE_BUGSNAG_ENABLE_IN_DEV?.trim().toLowerCase() === 'true'
  const enabledReleaseStages = enableInDev
    ? ['production', 'staging', 'uat', 'development']
    : ['production', 'staging', 'uat']

  Bugsnag.start({
    apiKey,
    plugins: [new BugsnagPluginReact(React)],
    releaseStage,
    enabledReleaseStages,
    appType: BUXUS_BUGSNAG_PLATFORM,
    appVersion: BUXUS_APP_DISPLAY_VERSION,
    // Collect client IP so Device/Request summaries aren't empty for web events.
    collectUserIp: true,
    metadata: {
      buxus: {
        platform: BUXUS_BUGSNAG_PLATFORM,
      },
    },
    onError(event) {
      event.app.type = BUXUS_BUGSNAG_PLATFORM
      event.addMetadata('buxus', { platform: BUXUS_BUGSNAG_PLATFORM })

      // Mapbox GL JS telemetry race: singleton MapLoadEvent/MapSessionAPI sets errorCb=null on
      // map.remove(), then a late auth/load callback still invokes it → unhandled TypeError.
      // Not actionable in app code; still present upstream as of mapbox-gl 3.x.
      const primaryMessage = event.errors?.[0]?.errorMessage ?? ''
      if (/errorCb is not a function/i.test(primaryMessage)) {
        return false
      }

      // Browser device metadata — Bugsnag's web notifier often leaves Manufacturer/Model empty.
      if (typeof navigator !== 'undefined') {
        const ua = navigator.userAgent || ''
        const platform = navigator.platform || ''
        let browser = 'unknown'
        if (/Edg\//.test(ua)) browser = 'Edge'
        else if (/Chrome\//.test(ua) && !/Chromium\//.test(ua)) browser = 'Chrome'
        else if (/Safari\//.test(ua) && !/Chrome\//.test(ua)) browser = 'Safari'
        else if (/Firefox\//.test(ua)) browser = 'Firefox'

        let os = 'unknown'
        if (/iPhone|iPad|iPod/.test(ua)) os = 'iOS'
        else if (/Android/.test(ua)) os = 'Android'
        else if (/Mac OS X|Macintosh/.test(ua)) os = 'macOS'
        else if (/Windows/.test(ua)) os = 'Windows'
        else if (/Linux/.test(ua)) os = 'Linux'

        event.addMetadata('device', {
          userAgent: ua,
          platform,
          language: navigator.language || null,
          browser,
          os,
          vendor: navigator.vendor || null,
          screen: typeof window !== 'undefined'
            ? `${window.screen?.width ?? '?'}x${window.screen?.height ?? '?'}`
            : null,
          href: typeof window !== 'undefined' ? window.location?.href ?? null : null,
        })
      }
    },
  })
  bugsnagStarted = true
}

export function isWebErrorReportingEnabled(): boolean {
  return bugsnagStarted
}

export function leaveWebBreadcrumb(
  name: string,
  metadata?: Record<string, unknown>,
  type: 'navigation' | 'request' | 'process' | 'log' | 'user' | 'state' | 'error' | 'manual' = 'manual',
): void {
  if (!bugsnagStarted) return
  Bugsnag.leaveBreadcrumb(name, metadata, type)
}

export function notifyWebError(
  error: unknown,
  options?: {
    context?: string
    metadata?: Record<string, Record<string, unknown>>
    severity?: 'info' | 'warning' | 'error'
  },
): void {
  if (!bugsnagStarted) {
    if (import.meta.env.DEV) {
      console.error('[errors] notifyWebError (Bugsnag off)', error, options)
    }
    return
  }

  const notifiable =
    error instanceof Error ? error : new Error(typeof error === 'string' ? error : String(error))

  Bugsnag.notify(notifiable, (event) => {
    if (options?.context) {
      event.context = options.context
    }
    if (options?.severity) {
      event.severity = options.severity
    }
    if (options?.metadata) {
      for (const [section, data] of Object.entries(options.metadata)) {
        event.addMetadata(section, data)
      }
    }
  })
  markWebErrorReported(notifiable)
  if (error !== notifiable) {
    markWebErrorReported(error)
  }
}

type ErrorBoundaryProps = {
  children?: ReactNode
}

/** React error boundary when Bugsnag is enabled; passthrough fragment otherwise. */
export function WebErrorBoundary({ children }: ErrorBoundaryProps): ReactNode {
  if (!bugsnagStarted) {
    return children
  }
  const plugin = Bugsnag.getPlugin('react')
  if (!plugin) {
    return children
  }
  const Boundary = plugin.createErrorBoundary(React)
  return React.createElement(Boundary, null, children)
}

export function setWebErrorUser(id: string | null | undefined, email?: string | null): void {
  if (!bugsnagStarted) return
  if (!id?.trim()) {
    Bugsnag.setUser(undefined, undefined, undefined)
    return
  }
  Bugsnag.setUser(id.trim(), email?.trim() || undefined, undefined)
}
